/**
 * Delete Chat Confirmation Modal Component
 * 
 * Confirmation modal for deleting a chat conversation.
 */

import React from 'react';
import { Modal, App } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import type { Conversation } from '@municipality/types';

interface DeleteChatConfirmationModalProps {
  open: boolean;
  conversation: Conversation | null;
  onClose: () => void;
  onConfirm: (conversationId: string) => Promise<void>;
}

export const DeleteChatConfirmationModal: React.FC<DeleteChatConfirmationModalProps> = ({
  open,
  conversation,
  onClose,
  onConfirm,
}) => {
  const { t } = useTranslation('chat');
  const { message } = App.useApp();
  const [isDeleting, setIsDeleting] = React.useState(false);

  const handleConfirm = async () => {
    if (!conversation) return;

    setIsDeleting(true);
    try {
      await onConfirm(conversation.id);
      message.success(
        conversation.type === 'group'
          ? t('deleteChat.successGroup') || 'Group chat deleted successfully'
          : t('deleteChat.successDirect') || 'Chat deleted successfully'
      );
      onClose();
    } catch (error) {
      console.error('Failed to delete chat:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to delete chat';
      message.error(errorMessage);
    } finally {
      setIsDeleting(false);
    }
  };

  if (!conversation) {
    return null;
  }

  const conversationName =
    conversation.type === 'group'
      ? conversation.name || 'Group Chat'
      : 'this chat';

  return (
    <Modal
      title={
        <span>
          <ExclamationCircleOutlined style={{ color: '#ff4d4f', marginRight: 8 }} />
          {conversation.type === 'group'
            ? t('deleteChat.titleGroup') || 'Delete group chat'
            : t('deleteChat.titleDirect') || 'Delete chat'}
        </span>
      }
      open={open}
      onOk={handleConfirm}
      onCancel={onClose}
      confirmLoading={isDeleting}
      okText={t('deleteChat.confirm') || 'Delete'}
      cancelText={t('buttons.cancel', { ns: 'common' }) || 'Cancel'}
      okButtonProps={{ danger: true }}
    >
      <p>
        {conversation.type === 'group'
          ? t('deleteChat.messageGroup', { name: conversationName }) ||
            `Are you sure you want to delete "${conversationName}"? This will permanently delete all messages and cannot be undone.`
          : t('deleteChat.messageDirect') ||
            'Are you sure you want to delete this chat? This will permanently delete all messages and cannot be undone.'}
      </p>
    </Modal>
  );
};

